import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useAuthContext } from './AuthContext';

const OnboardingContext = createContext({});

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider');
  }
  return context;
};

// Storage keys namespaced per user
const TOUR_STATUS_KEY = 'onboardingTourStatus'; // 'completed' | 'skipped' | null
const TOUR_STEP_KEY = 'onboardingTourStep'; // current step number for resume

export const OnboardingProvider = ({ children }) => {
  const { user } = useAuthContext();
  
  // Tour state
  const [isTourActive, setIsTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [pendingTourStart, setPendingTourStart] = useState(false);

  // Storage keys with user namespace
  const tourStatusKey = useMemo(() => {
    const uid = user?.id;
    return uid ? `${TOUR_STATUS_KEY}:${uid}` : TOUR_STATUS_KEY;
  }, [user?.id]);

  const tourStepKey = useMemo(() => {
    const uid = user?.id;
    return uid ? `${TOUR_STEP_KEY}:${uid}` : TOUR_STEP_KEY;
  }, [user?.id]);

  // Check tour status on mount — new user gets pending tour start (single Intro.js modal)
  // DISABLED: Tour auto-start disabled until ready to enable
  useEffect(() => {
    if (!user?.id) return;
    try {
      // const status = localStorage.getItem(tourStatusKey);
      // if (!status) {
      //   setPendingTourStart(true);
      // }
      const savedStep = localStorage.getItem(tourStepKey);
      if (savedStep) {
        setTourStep(parseInt(savedStep, 10) || 0);
      }
    } catch (_) {}
  }, [user?.id, tourStatusKey, tourStepKey]);

  const startTour = useCallback(() => {
    setPendingTourStart(false);
    setIsTourActive(true);
    setTourStep(0);
    try { localStorage.removeItem(tourStepKey); } catch (_) {}
  }, [tourStepKey]);

  /** "Take the tour" from menu: go to dashboard and start tour */
  const requestTourStart = useCallback(() => {
    setPendingTourStart(true);
  }, []);

  const completeTour = useCallback(() => {
    setIsTourActive(false);
    setTourStep(0);
    try {
      localStorage.setItem(tourStatusKey, 'completed');
      localStorage.removeItem(tourStepKey);
    } catch (_) {}
  }, [tourStatusKey, tourStepKey]);

  const skipTour = useCallback(() => {
    setIsTourActive(false);
    setTourStep(0);
    try {
      localStorage.setItem(tourStatusKey, 'skipped');
      localStorage.removeItem(tourStepKey);
    } catch (_) {}
  }, [tourStatusKey, tourStepKey]);

  const exitTour = useCallback(() => {
    // Exit without marking as completed/skipped (can resume later)
    setIsTourActive(false);
    try {
      localStorage.setItem(tourStepKey, String(tourStep));
    } catch (_) {}
  }, [tourStep, tourStepKey]);

  const setCurrentStep = useCallback((step) => {
    setTourStep(step);
  }, []);

  const resetTourStatus = useCallback(() => {
    try {
      localStorage.removeItem(tourStatusKey);
      localStorage.removeItem(tourStepKey);
    } catch (_) {}
    setTourStep(0);
    setPendingTourStart(true);
  }, [tourStatusKey, tourStepKey]);

  const value = useMemo(() => ({
    isTourActive,
    tourStep,
    pendingTourStart,
    startTour,
    requestTourStart,
    completeTour,
    skipTour,
    exitTour,
    setCurrentStep,
    resetTourStatus,
  }), [
    isTourActive,
    tourStep,
    pendingTourStart,
    startTour,
    requestTourStart,
    completeTour,
    skipTour,
    exitTour,
    setCurrentStep,
    resetTourStatus,
  ]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};



